import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  PackageImageUpload,
  createPackage,
  getPackageByID,
  updatePackage,
} from "./services/package.services";

import { clearPackageDetails } from "./packageSlice";
// import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { getFile } from "../../_helper/helper";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormDatePicker from "../../components/formFields/FormDateField";
import RoomPricingArray from "./RoomPricingArray";
import moment from "moment";
import FormCheckbox from "../../components/formFields/FormCheckbox";

const CreatePackage: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const PackageID = location.state?.PackageID;
  const PackageDetail = useAppSelector((state) => state.package.PackageDetail);
  const loading = useAppSelector((state) => state.package.loading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PackageDetail]);

  useEffect(() => {
    if (PackageID) {
      dispatch(getPackageByID(PackageID));
    }
    return () => {
      dispatch(clearPackageDetails());
    };
  }, [PackageID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      PackageTitle: values?.PackageTitle ? values?.PackageTitle : "",
      PackageURL: values?.PackageURL ? values?.PackageURL : "",
      City: values?.City ? values?.City : "",
      ValidityPeriod: values?.ValidityPeriod
        ? moment(values?.ValidityPeriod).format("YYYY-MM-DD")
        : null,
      PackageInclusion: values?.PackageInclusion
        ? values?.PackageInclusion
        : "",
      ChildPolicy: values?.ChildPolicy ? values?.ChildPolicy : "",
      CancellationPolicy: values?.CancellationPolicy
        ? values?.CancellationPolicy
        : "",
      CheckinCheckoutInfo: values?.CheckinCheckoutInfo
        ? values?.CheckinCheckoutInfo
        : "",
      IsFree: values?.IsFree ? values?.IsFree : false,
      FreeTitle: values?.FreeTitle ? values?.FreeTitle : "",
      MetaTitle: values?.MetaTitle ? values?.MetaTitle : "",
      MetaKeywords: values?.MetaKeywords ? values?.MetaKeywords : "",
      MetaDescription: values?.MetaDescription ? values?.MetaDescription : "",
      Roompricingdetails: values?.Roompricingdetails
        ? values?.Roompricingdetails?.map((room: any) => {
            return {
              RoomCategory: room?.RoomCategory ? room?.RoomCategory : "",
              NoOfRooms: room?.NoOfRooms ? +room?.NoOfRooms : null,
              Meal: room?.Meal ? room?.Meal : "",
              OneNightRate: room?.OneNightRate ? +room?.OneNightRate : null,
              OneNightRateType: room?.OneNightRateType
                ? room?.OneNightRateType
                : "",
              TwoNightRate: room?.TwoNightRate ? +room?.TwoNightRate : null,
              TwoNightRateType: room?.TwoNightRateType
                ? room?.TwoNightRateType
                : "",
            };
          })
        : [],
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage)
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? getFile(values?.BannerImage)
        : "";

    try {
      if (PackageID) {
        const editPayload = {
          ...formData,
          ID: PackageID,
        };

        const response = await dispatch(updatePackage(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.BannerImage && typeof values?.BannerImage !== "string")
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ID", PackageID);
            await dispatch(PackageImageUpload(ImageData));
          }
          navigate("/package");
          dispatch(clearPackageDetails());
        }
      } else {
        const response = await dispatch(createPackage(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.MainImage && typeof values?.MainImage !== "string") ||
              (values?.BannerImage &&
                typeof values?.BannerImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(PackageImageUpload(ImageData));
          }
          navigate("/package");
          dispatch(clearPackageDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={PackageDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12, marginBottom: 20 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {PackageID ? "Update Package" : "Add Package"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="PackageTitle"
                      label="Package Title"
                      placeholder="Package Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      maxLength={150}
                      onChange={(e: any) => {
                        formRenderProps.onChange("PackageURL", {
                          value: e?.value
                            ? e?.value
                                ?.toLowerCase()
                                ?.trim(" ")
                                ?.replace(/\s+/g, "-")
                            : "",
                        });
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="PackageURL"
                      label="Package URL"
                      placeholder="Package URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onInput={(e: any) => {
                        e.target.value = e?.target?.value
                          ?.toLowerCase()
                          ?.trim(" ")
                          ?.replace(/\s+/g, "-");
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="City"
                      label="Place"
                      placeholder="Place"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="ValidityPeriod"
                      label="Validity Period"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                      // validator={requiredValidator}
                      // astrike={true}
                      minDate={moment().toDate()}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="PackageInclusion"
                      label="Package Inclusion"
                      placeholder="Package Inclusion"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="ChildPolicy"
                      label="Child Policy"
                      placeholder="Child Policy"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="CancellationPolicy"
                      label="Cancellation Policy"
                      placeholder="Cancellation Policy"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="CheckinCheckoutInfo"
                      label="Checkin-Checkout Info"
                      placeholder="Checkin-Checkout Info"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{
                      marginTop: "18px",
                      background: "#edeaea",
                      borderRadius: 10,
                      padding: "0 10px 15px",
                      fontWeight: 500,
                      color: "black",
                    }}
                  >
                    <Field
                      labelClassName="ms-2"
                      name="IsFree"
                      label="Is Free"
                      placeholder="Is Free"
                      maxLength="100"
                      component={FormCheckbox}
                      onChange={() => {
                        formRenderProps.onChange("FreeTitle", {
                          value: "",
                        });
                      }}
                    />
                  </GridLayoutItem>
                  {formRenderProps.valueGetter("IsFree") && (
                    <GridLayoutItem colSpan={3}>
                      <Field
                        name="FreeTitle"
                        label="Free Title"
                        placeholder="Free Title"
                        component={FormRichTextField}
                        validator={requiredValidator}
                        style={{ height: 200 }}
                      />
                    </GridLayoutItem>
                  )}
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Keywords"
                      placeholder="Meta Keywords"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta Description"
                      component={FormTextArea}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      alternateName="Main Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Banner Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3} className="mt-3">
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={RoomPricingArray}
                      name="Roompricingdetails"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 20,
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={PackageID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/package")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreatePackage;
